import { CustomLayerStyle, groupBy, ICrop, IDataLayer, IDatalayersApiLoader, IDataLayerType, IGenericPlan, IOmniaContext, IOmniaHttpService, isArray, isArrayEmpty, isUndefinedOrNull, LayerTypeId, omniaAllSeries, omniaDefer, OmniaDefer, OmniaPromise, omniaResolve, selectMany } from "../omnia"
import { IFieldsService } from "../types"
import { CustomLayerTypeProvider, setupCustomLayerTypes } from "./customTypes"

import m = omnia.models

interface GetLayersOptions {
    layersPerRequest?: number,
    parallelQueries: number,
    blockUI?: boolean
}

interface ServiceData {
    LayerTypes: IDataLayerType[]
    CropYieldScales: m.ICropYieldScale[]
    GenericPlans: IGenericPlan[]
    CustomLayerTypes: CustomLayerStyle[]
}

export class LayerApi {

    private layerTypes: IDataLayerType[] = []
    private crops: ICrop[]
    private yieldScales: m.ICropYieldScale[]
    private plans: IGenericPlan[]

    private customStyles: CustomLayerStyle[]
    private customLayerTypeProvider: CustomLayerTypeProvider


    constructor(
        private readonly $q: ng.IQService,
        private readonly loader: IDatalayersApiLoader,
        private readonly ohttp: IOmniaHttpService,
        private readonly fieldSvc: IFieldsService,
        private readonly blockUI: { start(): void, stop(): void },
        private readonly trace: boolean) {


    }

    public load(context: IOmniaContext): ng.IPromise<any> {

        return this.getServiceData(context)
            .then(data => {

                this.crops = this.fieldSvc.crops

                const types = this.layerTypes = data.LayerTypes ?? []
                const customTypes = this.customStyles = data.CustomLayerTypes ?? []
                this.customLayerTypeProvider = setupCustomLayerTypes(types, customTypes)

                this.yieldScales = setupYieldScales(data.CropYieldScales)

                this.plans = data.GenericPlans ?? []

            })

    }

    private getServiceData(context: IOmniaContext): ng.IPromise<ServiceData> {

        const url = `/api/datalayers/service/data/?holdingId=${context.holdingId}`

        return this.ohttp.get(url).then(r => r.data)

    }

    getLayerTypes() {
        return [...this.layerTypes]
    }

    getCustomLayerType(customTypeId: number, typeId: LayerTypeId): IDataLayerType {

        const { customLayerTypeProvider } = this

        return customLayerTypeProvider
            ? customLayerTypeProvider(customTypeId, typeId)
            : undefined

    }

    getGenericPlans(yearId?: number): IGenericPlan[] {
        return yearId
            ? this.plans.filter(p => p.YearId == yearId)
            : [...this.plans]
    }

    getCropYieldScales(year?: number): m.ICropYieldScale[] {
        return isUndefinedOrNull(year)
            ? this.yieldScales.slice()
            : this.yieldScales.filter(s => s.Years && s.Years.some(y => y == year))
    }

    getLayers(ids: number[], options?: GetLayersOptions): PromiseLike<IDataLayer[]> {

        const layers = ids.map(id => ({ id, onLoaded: () => { } }))

        return this.getLayersInternal(layers, options)

    }

    getLayersInternal(
        layers: { id: number, onLoaded: (layers: IDataLayer[]) => void }[],
        { layersPerRequest = 20, parallelQueries = 5, blockUI = true } = {}
    ): PromiseLike<IDataLayer[]> {

        const { blockUI: _blockUI, trace } = this

        const groups = groupBy(
            [...layers]
                .sort((a, b) => a.id - b.id)
                .map((layer, index) => ({ layer, group: Math.floor(index / layersPerRequest) })),
            g => g.group
        )

        const queries = groups.map(g => ({

            url: "/api/datalayers/layer/?ids=" + g.values.map(i => i.layer.id).join(","),

            onLoaded: (layers: IDataLayer[]) => {

                for (const i of g.values) {
                    i.layer.onLoaded(layers)
                }

            }

        }))

        const taskGroups = groupBy(
            queries.map((query, index) => ({ query, group: Math.floor(index / parallelQueries) })),
            g => g.group
        )

        const tasks = taskGroups.map(g => g.values.map(v => v.query))

        if (trace) {
            console.log("getLayers 1", { layers, layersPerQuery: layersPerRequest, parallelQueries, tasks })
        }

        const start = performance.now()

        blockUI && _blockUI.start()

        const result = omniaAllSeries(
            tasks,
            t => this.$q.all(t.map(q =>

                this.fetchLayers(q.url, { blockUI })
                    // .then(l => trace ? wait(1000).then(() => l) : l)
                    .then(l => {
                        q.onLoaded(l)
                        return l
                    })

            ))

        )

        return result
            .then((l: IDataLayer[][][]) => {

                if (trace) {
                    const end = performance.now()
                    console.log("getLayers 2", { count: layers.length, start, end, duration: end - start })
                }

                return selectMany(l.map(g => selectMany(g)))

            })
            .finally(() => blockUI && _blockUI.stop())

    }

    private buffer: { queries: Map<number, OmniaDefer<IDataLayer[]>> } = undefined

    getLayersBuffered(ids: number[], blockUI = true): OmniaPromise<IDataLayer[]> {

        let { buffer } = this

        if (!buffer) {

            buffer = this.buffer = { queries: new Map() }

            window.requestAnimationFrame(() => {

                const layers = [...buffer.queries.entries()].map(([id, defer]) => ({ id, onLoaded: (l: IDataLayer[]) => defer.resolve(l) }))

                this.buffer = undefined


                this.getLayersInternal(
                    layers,
                    { layersPerRequest: 5, blockUI }
                )

            })

        }

        const defer = omniaDefer<IDataLayer[]>()

        const allDeferred = new Set<OmniaDefer<IDataLayer[]>>()

        for (const id of ids) {

            const pending = buffer.queries.get(id)

            if (pending) {
                allDeferred.add(buffer.queries.get(id))
            }
            else {
                buffer.queries.set(id, defer)
                allDeferred.add(defer)
            }

        }

        const size = allDeferred.size

        if (size == 0) {
            return omniaResolve([])
        }

        if (size == 1) {
            return [...allDeferred.values()][0].promise
        }

        const allPromises = [...allDeferred.values()].map(v => v.promise)

        return this.$q.all(allPromises).then(r => selectMany(r))

    }

    getFieldLayers(fieldIds: number[]): ng.IPromise<IDataLayer[]> {

        if (isArrayEmpty(fieldIds)) {
            return this.$q.resolve([])
        }

        const ids = fieldIds.map(id => id.toString()).join(",")

        const url = "/api/datalayers/layer/field/" + ids

        return this.fetchLayers(url)

    }

    getHoldingLayers(holdingId: number, yearId: number): ng.IPromise<IDataLayer[]> {

        const url = "/api/datalayers/layer/holding/" + holdingId + "/year/" + yearId

        return this.fetchLayers(url)

    }

    getHoldingYieldLayers(holdingId: number): ng.IPromise<IDataLayer[]> {

        const url = "/api/datalayers/layer/holding/yield/" + holdingId

        return this.fetchLayers(url)

    }

    getLayerManagerEntries(holdingId: number): ng.IPromise<IDataLayer[]> {

        const url = "/api/layers/manager/holding/" + holdingId

        return this.fetchLayers(url, { map: l => l })

    }


    private fetchLayers(url: string, { blockUI = true, map = (a: any) => a.Layer as ((data: any) => IDataLayer) } = {}): ng.IPromise<IDataLayer[]> {

        return this.ohttp.get(url, blockUI).then(r => {

            if (!r.data) return

            let array: any[] = omnia.isArray(r.data) ? r.data : [r.data]

            array = array.map(map)

            const layers = this.setupLayers(array)

            return layers

        })

    }

    loadLayerPoints(layerId: number): PromiseLike<number[][]> {

        const url = `/api/layers/points?layerId=${layerId}`

        return this.ohttp.get(url).then(r => r.data)

    }

    setupLayers(layers: IDataLayer[]): IDataLayer[] {

        const { loader, layerTypes, customStyles, crops, plans, fieldSvc } = this

        const context = {
            layerTypes,
            customStyles,
            getCustomLayerType: (id: number, type: LayerTypeId) => this.getCustomLayerType(id, type),
            crops: crops,
            cropping: fieldSvc.cropping,
            plans
        }

        layers = omnia.datalayers.setupLayers(layers, context)

        layers = loader.loadLayers(layers, context)

        return layers

    }

    public refreshGenericPlans(refresh: { UpdatedPlans: IGenericPlan[], DeletedPlans: number[] }) {

        const updated = refresh.UpdatedPlans

        if (isArray(updated)) {
            updated.forEach(p => this.refreshGenericPlan(p))
        }

        const deleted = refresh.DeletedPlans

        if (isArray(deleted)) {
            this.plans = this.plans.filter(p => deleted.indexOf(p.GenericPlanId) < 0)
        }


    }

    private refreshGenericPlan(plan: IGenericPlan) {

        if (!plan) return

        const current = this.plans.find(p => p.GenericPlanId == plan.GenericPlanId)

        if (current) {
            angular.extend(current, plan)
            return current
        }
        else {
            this.plans.push(plan)
            return plan
        }

    }

}

function setupYieldScales(scales: m.ICropYieldScale[]) {

    return (scales || []).map(s => ({
        ...s,
        Years: s.Years || [],
    }))
}
